import type { CacheEntry } from './types.ts';

export function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export function completed(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
    tx.onerror = () => reject(tx.error);
  });
}

export function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open('replay-cache', 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('entries')) db.createObjectStore('entries', { keyPath: 'sha256' });
      if (!db.objectStoreNames.contains('chunks')) db.createObjectStore('chunks');
    };
    req.onsuccess = () => { req.result.onversionchange = () => req.result.close(); resolve(req.result); };
    req.onerror = () => reject(req.error);
  });
}

export class MetadataIndex {
  constructor(private readonly db: IDBDatabase) {}
  async get(key: string): Promise<CacheEntry | null> {
    return (await request(this.db.transaction('entries').objectStore('entries').get(key))) ?? null;
  }
  async list(): Promise<CacheEntry[]> {
    return request(this.db.transaction('entries').objectStore('entries').getAll());
  }
  async put(entry: CacheEntry): Promise<void> {
    const tx = this.db.transaction('entries', 'readwrite');
    const done = completed(tx);
    const store = tx.objectStore('entries');
    const existing = await request<CacheEntry | undefined>(store.get(entry.sha256));
    // A checkpoint must preserve retention edits made while the network was busy.
    if (existing) {
      entry.pinned = existing.pinned;
      entry.gameIds = [...new Set([...existing.gameIds, ...entry.gameIds])];
    }
    store.put(entry);
    await done;
  }
  async update(key: string, change: (entry: CacheEntry) => void): Promise<CacheEntry | null> {
    const tx = this.db.transaction('entries', 'readwrite');
    const done = completed(tx);
    const store = tx.objectStore('entries');
    const entry = await request<CacheEntry | undefined>(store.get(key));
    if (entry) { change(entry); store.put(entry); }
    await done;
    return entry ?? null;
  }
  async remove(key: string): Promise<void> {
    const tx = this.db.transaction('entries', 'readwrite');
    const done = completed(tx);
    tx.objectStore('entries').delete(key);
    await done;
  }
}
